import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { APP_CONFIG, HTTP_METHOD, REDUX_CACHE } from '../constants';
import * as endpoints from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
  getBaseQueryError,
} from '../generic';
import { IAppVariable } from '../../../types/interfaces/app-variable';
import { IAppFeature } from '../../../types/interfaces/app-feature';
import { IAiPrompt } from '../../../types/interfaces/ai-prompt';
import { IExerciseLoadFormula } from '../../../types/interfaces/exercise-load-formula';
import { IFilePaths } from '../../../types/interfaces/file-paths';
import { IMigrateStoragePathsResponse } from '../../../types/interfaces/migrate-storage-paths-response';
import {
  errorToastMessage,
  successToastMessage,
} from '../../../components/layout/toast-root/toast';
import { Messages } from '../../../shared/messages';

export const appConfigApi = createApi({
  reducerPath: 'appConfigApi',
  tagTypes: [
    APP_CONFIG.APP_VARIABLES,
    APP_CONFIG.APP_VARIABLE,
    APP_CONFIG.APP_FEATURES,
    APP_CONFIG.APP_FEATURE,
    APP_CONFIG.AI_PROMPTS,
    APP_CONFIG.AI_PROMPT,
    APP_CONFIG.EXERCISE_LOAD_FORMULAS,
    APP_CONFIG.EXERCISE_LOAD_FORMULA,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(endpoints.AppCongig.ROOT),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getAppVariablesApi: builder.query<IAppVariable[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.AppCongig.VARIABLE,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, APP_CONFIG.APP_VARIABLES),
      ...TransformedErrorResponseWithNotification(),
    }),
    getAppVariableByIdApi: builder.query<IAppVariable, string>({
      keepUnusedDataFor: 3600,
      query(id: string) {
        return {
          url: endpoints.AppCongig.VARIABLE_ID(id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: APP_CONFIG.APP_VARIABLE }],
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutAppVariableApi: builder.mutation<
      void,
      {
        id: string;
        body: IAppVariable;
      }
    >({
      query({ id, body }) {
        return {
          url: `${id ? endpoints.AppCongig.VARIABLE_ID(id) : endpoints.AppCongig.VARIABLE}`,
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: APP_CONFIG.APP_VARIABLES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: APP_CONFIG.APP_VARIABLE,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    deleteAppVariableApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: endpoints.AppCongig.VARIABLE_ID(id),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: APP_CONFIG.APP_VARIABLES,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    getAiPromptsApi: builder.query<IAiPrompt[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.AppCongig.AI_PROMPTS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, APP_CONFIG.AI_PROMPTS),
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutAiPromptApi: builder.mutation<
      void,
      { id?: string; body: { key: string; value: string } }
    >({
      query({ id, body }) {
        return {
          url: id
            ? endpoints.AppCongig.AI_PROMPT_ID(id)
            : endpoints.AppCongig.AI_PROMPTS,
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            { type: APP_CONFIG.AI_PROMPTS, id: REDUX_CACHE.LIST },
            { type: APP_CONFIG.AI_PROMPT },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    deleteAiPromptApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: endpoints.AppCongig.AI_PROMPT_ID(id),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [{ type: APP_CONFIG.AI_PROMPTS, id: REDUX_CACHE.LIST }];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    getAppFeaturesApi: builder.query<IAppFeature[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.AppCongig.FEATURES,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, APP_CONFIG.APP_FEATURES),
      ...TransformedErrorResponseWithNotification(),
    }),
    getAppFeatureByIdApi: builder.query<IAppFeature, string>({
      keepUnusedDataFor: 3600,
      query(id: string) {
        return {
          url: endpoints.AppCongig.FEATURE_ID(id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => [{ type: APP_CONFIG.APP_FEATURE }],
      ...TransformedErrorResponseWithNotification(),
    }),
    putAppFeatureApi: builder.mutation<
      IAppFeature,
      { id: string; body: { isActive: boolean } }
    >({
      query({ id, body }) {
        return {
          url: endpoints.AppCongig.FEATURE_ID(id),
          method: HTTP_METHOD.PUT,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: APP_CONFIG.APP_FEATURES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: APP_CONFIG.APP_FEATURE,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    getExerciseLoadFormulasApi: builder.query<IExerciseLoadFormula[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.AppCongig.FORMULAS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, APP_CONFIG.EXERCISE_LOAD_FORMULAS),
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutExerciseLoadFormulaApi: builder.mutation<
      void,
      {
        id: string;
        body: IExerciseLoadFormula;
      }
    >({
      query({ id, body }) {
        return {
          url: `${id ? endpoints.AppCongig.FORMULA_ID(id) : endpoints.AppCongig.FORMULAS}`,
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      transformResponse: (response: any, meta: any): any => {
        let message = '';
        const method = meta.request.method;

        switch (method) {
          case HTTP_METHOD.POST:
            message = `Exercise load formula ${Messages.dataCreated}`;
            break;
          case HTTP_METHOD.PUT:
            message = `Exercise load formula ${Messages.dataUpdated}`;
            break;
        }

        successToastMessage(message);
        return { id: response?.id, status: meta?.response?.status };
      },
      transformErrorResponse: (response: any, meta: any) => {
        const error = getBaseQueryError(response);

        if (meta?.response?.status === 400) {
          return {
            message: error?.message,
            status: meta?.response?.status,
            data: {
              message: error?.message,
            },
          };
        }

        errorToastMessage(error?.message);

        return { message: error?.message, status: meta?.response?.status };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: APP_CONFIG.EXERCISE_LOAD_FORMULAS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: APP_CONFIG.EXERCISE_LOAD_FORMULA,
            },
          ];
        }
      },
    }),
    deleteExerciseLoadFormulaApi: builder.mutation<void, string>({
      query(id) {
        return {
          url: endpoints.AppCongig.FORMULA_ID(id),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: APP_CONFIG.EXERCISE_LOAD_FORMULAS,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    resetCacheApi: builder.mutation<boolean, void>({
      query() {
        return {
          url: endpoints.AppCongig.CACHE_RESET,
          method: HTTP_METHOD.POST,
        };
      },
      ...TransformedResponseWithNotification('Cache'),
    }),
    getFilePathsApi: builder.query<IFilePaths, void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.AppCongig.FILE_PATHS,
          method: HTTP_METHOD.GET,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    verifyFirebaseUserEmailApi: builder.mutation<boolean, { email: string }>({
      query({ email }) {
        return {
          url: endpoints.AppCongig.FIREBASE_USER_VERIFY_EMAIL,
          method: HTTP_METHOD.POST,
          body: { email },
        };
      },
    }),
    migratePublicStoragePathsApi: builder.mutation<
      IMigrateStoragePathsResponse,
      void
    >({
      query() {
        return {
          url: endpoints.AppCongig.MIGRATE_PUBLIC_STORAGE_PATHS,
          method: HTTP_METHOD.POST,
        };
      },
    }),
    migrateOwnerStoragePathsApi: builder.mutation<
      IMigrateStoragePathsResponse,
      void
    >({
      query() {
        return {
          url: endpoints.AppCongig.MIGRATE_OWNER_STORAGE_PATHS,
          method: HTTP_METHOD.POST,
        };
      },
    }),
  }),
});

export const {
  useGetAppVariablesApiQuery: useGetAppVariables,
  useGetAppVariableByIdApiQuery: useGetAppVariableById,
  usePostPutAppVariableApiMutation: usePostPutAppVariable,
  useDeleteAppVariableApiMutation: useDeleteAppVariable,
  useGetAiPromptsApiQuery: useGetAiPrompts,
  usePostPutAiPromptApiMutation: usePostPutAiPrompt,
  useDeleteAiPromptApiMutation: useDeleteAiPrompt,
  useGetAppFeaturesApiQuery: useGetAppFeatures,
  useGetAppFeatureByIdApiQuery: useGetAppFeatureById,
  usePutAppFeatureApiMutation: usePutAppFeature,
  useGetExerciseLoadFormulasApiQuery: useGetExerciseLoadFormulas,
  usePostPutExerciseLoadFormulaApiMutation: usePostPutExerciseLoadFormula,
  useDeleteExerciseLoadFormulaApiMutation: useDeleteExerciseLoadFormula,
  useResetCacheApiMutation: useResetCache,
  useGetFilePathsApiQuery: useGetFilePaths,
  useVerifyFirebaseUserEmailApiMutation: useVerifyFirebaseUserEmail,
  useMigratePublicStoragePathsApiMutation: useMigratePublicStoragePaths,
  useMigrateOwnerStoragePathsApiMutation: useMigrateOwnerStoragePaths,
} = appConfigApi;
